import { useMemo, useState, type ComponentType } from 'react';
import Layout from '@/layouts/Layout';
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ActivityTrendChart } from '@/components/analytics/ActivityTrendChart';
import { BookOpen, CheckCircle2, MessageCircle, Users } from 'lucide-react';
import { Link } from '@inertiajs/react';
import { Heading } from '@/components/ui/heading';
import { ActivityFeedItem } from '@/components/timeline/ActivityFeedItem';
import { TimelineDayGroup } from '@/components/timeline/TimelineDayGroup';
import { getCalendarBadgeParts } from '@/lib/date';
import type { ActivityDayGroupWithPatients, ActivityEvent } from '@/lib/activityFeed';
import { groupActivityEvents } from '@/lib/activityFeed';

type WindowKey = 'last24h' | 'last7d';

type CountsWindow = {
  chats_started: number;
  journals_started: number;
  journals_completed: number;
  active_patients: number;
};

type TrendPoint = {
  date: string;
  chats_started: number;
  journals_completed: number;
};

type DashboardProps = {
  therapist: { id: number; name: string };
  counts: Record<WindowKey, CountsWindow>;
  trend: TrendPoint[];
  feed: ActivityEvent[];
  demo?: {
    has_demo: boolean;
    demo_patient_id?: number | null;
  };
};

const WINDOW_LABELS: Record<WindowKey, string> = {
  last24h: 'Ultime 24h',
  last7d: 'Ultimi 7 giorni',
};

function StatCard({
  label,
  value,
  icon: Icon,
  accent,
}: {
  label: string;
  value: number | string;
  icon: ComponentType<{ className?: string }>;
  accent: string;
}) {
  return (
    <Card className="dark:border-slate-800 dark:bg-slate-900/70 bg-slate-100 border">
      <CardContent className="flex items-center justify-between pt-4">
        <div>
          <p className="text-sm dark:text-slate-400 text-slate-500">{label}</p>
          <p className="text-2xl font-semibold dark:text-slate-50 text-slate-800">{value}</p>
        </div>
        <div className={`flex h-10 w-10 items-center justify-center rounded-full ${accent}`}>
          <Icon className="h-5 w-5 dark:text-slate-50 text-slate-500" />
        </div>
      </CardContent>
    </Card>
  );
}

export default function Dashboard({ therapist, counts, trend, feed, demo }: DashboardProps) {
  const [windowKey, setWindowKey] = useState<WindowKey>('last24h');

  const currentCounts = useMemo(() => counts[windowKey] || counts.last24h, [counts, windowKey]);
  const groupedFeed = useMemo(
    () => groupActivityEvents(feed, { byPatient: true }) as ActivityDayGroupWithPatients[],
    [feed],
  );
  return (
    <Layout isAdmin user={{ name: therapist.name }}>
      <div className="flex w-full flex-col gap-4">
        <header className="flex flex-col gap-2">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500">
            Eccoti di nuovo, {therapist.name}!
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <Heading>La tua dashboard</Heading>
          </div>
        </header>

        {demo?.has_demo && (
          <Card semantic="primary">
            <CardHeader>
              <CardTitle>Emovia ti da il benvenuto!</CardTitle>
            </CardHeader>
            <CardContent>
              Controlla il tuo account demo per esplorare tutte le funzionalità di Emovia. Abbiamo
              preparato dei clienti di prova con chat e diario già pronti: guarda la sezione
              relativa per provarli subito. Prova l&apos;AI e i flussi. Quando sei pronto, invita il
              tuo primo cliente reale.
              <CardAction className="pt-4">
                <Button asChild size="sm" color="primary">
                  <Link href="/therapists/patients">Gestione clienti</Link>
                </Button>
              </CardAction>
            </CardContent>
          </Card>
        )}

        <Tabs value={windowKey} onValueChange={(value) => setWindowKey(value as WindowKey)}>
          <TabsList>
            <TabsTrigger value="last24h">{WINDOW_LABELS.last24h}</TabsTrigger>
            <TabsTrigger value="last7d">{WINDOW_LABELS.last7d}</TabsTrigger>
          </TabsList>
          <TabsContent value={windowKey} className="space-y-4">
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
              <StatCard
                label="Chat iniziate"
                value={currentCounts.chats_started}
                icon={MessageCircle}
                accent="bg-sky-500/20"
              />
              <StatCard
                label="Diari avviati"
                value={currentCounts.journals_started}
                icon={BookOpen}
                accent="bg-emerald-500/20"
              />
              <StatCard
                label="Diari conclusi"
                value={currentCounts.journals_completed}
                icon={CheckCircle2}
                accent="bg-lime-500/20"
              />
              <StatCard
                label="Pazienti attivi"
                value={currentCounts.active_patients}
                icon={Users}
                accent="bg-indigo-500/20"
              />
            </div>

            <Card className="dark:border-slate-800 dark:bg-slate-900/70 border bg-slate-100">
              <CardHeader className="pb-2">
                <CardTitle className="dark:text-slate-100 text-slate-800">
                  Andamento 14 giorni
                </CardTitle>
                <CardDescription className="dark:text-slate-400 text-slate-500">
                  Chat vs diari completati (aggregati al giorno)
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                <ActivityTrendChart data={trend} height={220} />
              </CardContent>
            </Card>

            <Heading level={2}>Ultime attività</Heading>
            <div>
              {feed.length === 0 ? (
                <p className="text-sm text-slate-500">Nessuna attività recente.</p>
              ) : (
                <div className="relative grid grid-cols-1 gap-6">
                  {groupedFeed.map((group) => {
                    const badge = getCalendarBadgeParts(group.date);
                    const dayNumber = badge?.day ?? '--';
                    const monthLabel = badge?.month ?? '—';
                    return (
                      <TimelineDayGroup key={group.key} day={dayNumber} month={monthLabel}>
                        <div className="space-y-4">
                          {group.patients.map((patient) => (
                            <div key={patient.id} className="space-y-2">
                              <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                                <Link
                                  href={`/therapists/patients/${patient.id}`}
                                  className="inline-flex items-center gap-1 transition hover:text-slate-900 dark:hover:text-white"
                                >
                                  <span aria-hidden>👤</span>
                                  <span>{patient.name}</span>
                                </Link>
                              </p>
                              <div className="space-y-2">
                                {patient.events.map((event) => (
                                  <ActivityFeedItem key={event.id} event={event} />
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </TimelineDayGroup>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
